import { normalizeToProblemDetails } from '@quipex/core/utils';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    toQuippyDocumentOptions,
    toQuippyResponseContent,
    toQuippySources,
} from '../mapper';
import type { QuippyBuildingSession, QuippyScope } from '../models';
import type { QuippyApi } from '../transport';
import {
    buildConversationHistory,
    createLoadingAssistantMessage,
    findThread,
} from './helpers';
import { buildPostModel } from './scope';
import {
    addAssistantMessageToSession,
    updateAssistantMessageInSession,
} from './state';

const SELECTED_DOCUMENT_USER_INPUT = 'Use selected document';

export interface DocumentSelectionRequest {
    api: QuippyApi;
    scope: QuippyScope;
    chatId: string;
    messageId: string;
    fileId: string;
    getSession: (scope: QuippyScope) => QuippyBuildingSession;
    updateSession: (
        scope: QuippyScope,
        updater: (session: QuippyBuildingSession) => QuippyBuildingSession
    ) => void;
}

export function isDocumentSelectionPending(
    session: QuippyBuildingSession,
    chatId: string,
    messageId: string
): boolean {
    const thread = findThread(session, chatId);
    const message = thread?.messages.find((item) => item.id === messageId);

    return message?.documentSelectionStatus === 'pending';
}

export function hasPendingDocumentSelection(
    session: QuippyBuildingSession,
    chatId: string
): boolean {
    const thread = findThread(session, chatId);

    return !!thread?.messages.some(
        (message) => message.documentSelectionStatus === 'pending'
    );
}

export function processDocumentSelection$(request: DocumentSelectionRequest) {
    const thread = findThread(
        request.getSession(request.scope),
        request.chatId
    );
    if (!thread) return of(undefined);

    const messageIndex = thread.messages.findIndex(
        (message) => message.id === request.messageId
    );
    const message = thread.messages[messageIndex];
    const selectedOption = message?.documentOptions?.find(
        (option) => option.fileId === request.fileId
    );
    if (!message || !selectedOption) {
        return of(undefined);
    }

    updateDocumentSelection(request, request.fileId, 'pending');
    const userInput = buildSelectedDocumentUserInput(selectedOption);
    const pendingMessage = createLoadingAssistantMessage();
    request.updateSession(request.scope, (session) =>
        addAssistantMessageToSession(session, request.chatId, pendingMessage)
    );

    const payload = buildPostModel({
        scope: request.scope,
        buildingContext: request.getSession(request.scope).buildingContext,
        userInput,
        conversationHistory: buildConversationHistory(
            thread.messages.slice(0, messageIndex + 1)
        ),
        selectedFileId: request.fileId,
    });

    return request.api.ask(payload).pipe(
        tap((response) => {
            updateDocumentSelection(request, request.fileId, 'selected');
            request.updateSession(request.scope, (session) =>
                updateAssistantMessageInSession(
                    session,
                    request.chatId,
                    pendingMessage.id,
                    {
                        content: toQuippyResponseContent(response),
                        status: 'sent',
                        sources: toQuippySources(response.files ?? []),
                        documentOptions: toQuippyDocumentOptions(response),
                        retryInput: userInput,
                        retrySelectedFileId: request.fileId,
                    }
                )
            );
        }),
        catchError((error: unknown) => {
            const problemDetails = normalizeToProblemDetails(error);
            console.error('Document selection failed', problemDetails, error);
            updateDocumentSelection(request, undefined, 'idle');
            request.updateSession(request.scope, (session) =>
                updateAssistantMessageInSession(
                    session,
                    request.chatId,
                    pendingMessage.id,
                    {
                        content:
                            'Something went wrong. Please try your selection again.',
                        status: 'error',
                        sources: [],
                        retryInput: userInput,
                        retrySelectedFileId: request.fileId,
                    }
                )
            );

            return of(undefined);
        }),
        map(() => undefined)
    );
}

function buildSelectedDocumentUserInput(option: {
    buildingName?: string;
    fileId: string;
    title: string;
}): string {
    const buildingName = option.buildingName?.trim();
    const label = buildingName
        ? `${option.title} - ${buildingName}`
        : option.title;
    return `${SELECTED_DOCUMENT_USER_INPUT}: ${label} (${option.fileId})`;
}

function updateDocumentSelection(
    request: DocumentSelectionRequest,
    fileId: string | undefined,
    status: 'idle' | 'pending' | 'selected'
): void {
    request.updateSession(request.scope, (session) =>
        updateAssistantMessageInSession(
            session,
            request.chatId,
            request.messageId,
            {
                selectedDocumentFileId: fileId,
                documentSelectionStatus: status,
            }
        )
    );
}
